import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Loading from 'core-system/Loading'
import MyCommutersView from 'features/Segments/MyCommuters/MyCommutersView'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import styled from 'styled-components'
import { employeeActions } from 'redux/employee/employeeSlice'
import { employerActions } from 'redux/employer/employerSlice'
import WorksitesView from './worksites/WorksitesView'
import GroupsView from './groups/GroupsView'
import TagsView from './Tags/TagsView'
import SyncSettingsView from './syncSettings/SyncSettingsView'
import PayCadencePageView from 'features/PayCadence/PayCadencePageView'
import { isEmpty } from 'lodash-es'
import { partitionArchivedEmployees } from './helperFunctions'

const Container = styled.div`
  padding: 2rem;
`

const SegmentsView = React.memo(() => {
  const dispatch = useDispatch()

  const {
    myCommuters,
    allCommuters,
    archivedEmployees,
    declinedEmployees,
  } = useSelector((state: AppState) => state.employee)
  const {
    segmentsMap,
    allEmployeeSegmentId,
    employeesDeclinedFlag,
    finchConnectLoading,
  } = useSelector((state: AppState) => state.employer)

  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'worksites' ? 1 : 0
  )

  useEffect(() => {
    if (activeTab !== 0) return
    dispatch(employeeActions.getArchivedEmployees())
    dispatch(employeeActions.getDeclinedEmployees())
    dispatch(employeeActions.getCommuters())
  }, [activeTab, dispatch])

  useEffect(() => {
    if (activeTab !== 0 || !allEmployeeSegmentId) return
    if (
      !myCommuters ||
      (!myCommuters[allEmployeeSegmentId] &&
        Object.keys(myCommuters).length > 0) ||
      isEmpty(myCommuters)
    ) {
      dispatch(
        employeeActions.getMyCommuters({
          segment: allEmployeeSegmentId,
        })
      )
    }
  }, [activeTab, myCommuters, allEmployeeSegmentId, dispatch])

  useEffect(() => {
    if (employeesDeclinedFlag) {
      dispatch(employeeActions.getCommuters())
      dispatch(employerActions.toggleDeclineInactiveEmployeesFlag())
    }
  }, [employeesDeclinedFlag, dispatch])

  if (!allEmployeeSegmentId || !segmentsMap?.[allEmployeeSegmentId] || finchConnectLoading) {
    return <Loading fullPage />
  }

  const tabs = [
    { label: 'Commuters', value: 0 },
    { label: 'Worksites', value: 1 },
    { label: 'Groups', value: 2 },
    { label: 'Tags', value: 3 },
    { label: 'Sync Settings', value: 4 },
    { label: 'Pay Schedules', value: 5 },
  ]

  const commutersReady =
    myCommuters?.[allEmployeeSegmentId] &&
    allCommuters &&
    archivedEmployees &&
    declinedEmployees

  const pendingEmployees = commutersReady
    ? allCommuters.filter((commuter) => !commuter.approved)
    : []
  const { blockedEmployees, nonBlockedArchivedEmployees } = commutersReady
    ? partitionArchivedEmployees(archivedEmployees)
    : { blockedEmployees: [], nonBlockedArchivedEmployees: [] }

  return (
    <Container>
      {activeTab === 0 &&
        (commutersReady ? (
          <MyCommutersView
            myCommuters={myCommuters[allEmployeeSegmentId]}
            activeCommuters={myCommuters[allEmployeeSegmentId]}
            activeSegment={segmentsMap[allEmployeeSegmentId]}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            allEmployeeSegmentId={allEmployeeSegmentId}
            tabs={tabs}
            archivedEmployees={nonBlockedArchivedEmployees}
            blockedEmployees={blockedEmployees}
            declinedEmployees={declinedEmployees}
            pendingEmployees={pendingEmployees}
          />
        ) : (
          <Loading fullPage />
        ))}
      {activeTab === 1 && (
        <WorksitesView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
      {activeTab === 2 && (
        <GroupsView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
      {activeTab === 3 && (
        <TagsView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
      {activeTab === 4 && (
        <SyncSettingsView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
      {activeTab === 5 && (
        <PayCadencePageView
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  SegmentsView.displayName = 'SegmentsView'
}

export default SegmentsView
